/**
 * Utility functions for formatting and validation
 */

/**
 * Format kennitala (Icelandic SSN) for display
 * @param kennitala - Raw kennitala (e.g., "1906765529")
 * @returns Formatted kennitala (e.g., "190676-5529")
 */
export function formatKennitala(kennitala: string): string {
  if (!kennitala || kennitala.length !== 10) return kennitala;
  return `${kennitala.slice(0, 6)}-${kennitala.slice(6)}`;
}

/**
 * Validate kennitala format
 * @param kennitala - Kennitala to validate
 * @returns True if valid format
 */
export function isValidKennitala(kennitala: string): boolean {
  // Remove hyphen if present
  const cleaned = kennitala.replace('-', '');

  // Must be exactly 10 digits
  if (!/^\d{10}$/.test(cleaned)) return false;

  // Basic date validation (DDMMYY)
  const day = parseInt(cleaned.slice(0, 2));
  const month = parseInt(cleaned.slice(2, 4));

  if (day < 1 || day > 31) return false;
  if (month < 1 || month > 12) return false;

  return true;
}

/**
 * Format date for display (Icelandic format)
 * @param date - Date string or Date object
 * @returns Formatted date (e.g., "3. feb 2026")
 */
export function formatDate(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;

  return d.toLocaleDateString('is-IS', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * Format date and time for display
 * @param date - Date string or Date object
 * @returns Formatted date and time (e.g., "3. feb 2026, 14:30")
 */
export function formatDateTime(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;

  return d.toLocaleDateString('is-IS', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format currency (ISK)
 * @param amount - Amount in ISK
 * @returns Formatted currency (e.g., "15.000 kr")
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('is-IS', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount) + ' kr';
}

/**
 * Format phone number (Icelandic format)
 * @param phone - Phone number
 * @returns Formatted phone (e.g., "555-1234" or "821-2345")
 */
export function formatPhone(phone: string): string {
  const cleaned = phone.replace(/\D/g, '');

  if (cleaned.length === 7) {
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
  }

  return phone;
}

/**
 * Get initials from name
 * @param firstName - First name
 * @param lastName - Last name
 * @returns Initials (e.g., "JD")
 */
export function getInitials(firstName: string, lastName: string): string {
  return `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase();
}

/**
 * Truncate text with ellipsis
 * @param text - Text to truncate
 * @param maxLength - Maximum length
 * @returns Truncated text
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
}

/**
 * Calculate age from date of birth
 * @param dateOfBirth - Date of birth (ISO string or Date)
 * @returns Age in years
 */
export function calculateAge(dateOfBirth: string | Date): number {
  const dob = typeof dateOfBirth === 'string' ? new Date(dateOfBirth) : dateOfBirth;
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age--;
  }

  return age;
}

/**
 * Mask kennitala for privacy (show only last 4 digits)
 * @param kennitala - Full kennitala
 * @returns Masked kennitala (e.g., "******-5529")
 */
export function maskKennitala(kennitala: string): string {
  if (!kennitala || kennitala.length !== 10) return kennitala;
  return `******-${kennitala.slice(6)}`;
}

/**
 * Debounce function
 * @param func - Function to debounce
 * @param wait - Wait time in ms
 * @returns Debounced function
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout>;

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };

    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Class names helper (simple version)
 * @param classes - Class names
 * @returns Combined class names
 */
export function cn(...classes: (string | boolean | undefined)[]): string {
  return classes.filter(Boolean).join(' ');
}
